import {Injectable} from '@angular/core';
import {BaseJsonService} from '@shared/services/base.service';
import {Observable, of} from 'rxjs';
import {catchError, map, shareReplay} from 'rxjs/operators';
import {ListResponse, ObjectResponse} from '@shared/models';
import {SupplierDetailsModel} from 'src/models/supplier/supplier.details.model';
import {SelectItem} from 'src/models/general/select.item.model';
import {SupplierListViewModel} from 'src/models/supplier/supplier.list.view.model';
import {PurchaseCost} from 'src/models/stock/purchase.cost.model';
import {StockCategories} from 'src/models/stock/stockCategories.enum';


@Injectable({
	providedIn: 'root'
})
export class SupplierService extends BaseJsonService {

	// Iterating over StockCategories is more complex than necessary, to be consistent to the other enums
	// because of the way TypeScript transpiles Enums to JS - https://www.typescriptlang.org/docs/handbook/enums.html
	private stockCategoryNames: Map<number, string> = new Map<number, string>([
		[StockCategories.DISTRIBUTOR, 'Lieferant'],
		[StockCategories.SELF, 'Eigenlager'],
		[StockCategories.SALE, 'Abverkauf'],
		[StockCategories.VIRTUAL, 'Virtuelles Lager']
	]);

	private stockCategoriesSelectItems!: SelectItem[];

	getSupplierList(): Observable<SupplierDetailsModel[]> {
		const url = this.generateUrl(this.services.stock, 'supplier');

		return this.http.get<ListResponse<SupplierDetailsModel>>(url, this.options).pipe(
			map((response: ListResponse<SupplierDetailsModel>) => {
				return response.data || [];
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	getSupplierListSortedByName(ascending: boolean = true): Observable<SupplierDetailsModel[]> {
		return this.getSupplierList().pipe(
			map((supplierList: SupplierDetailsModel[]) => {
				return supplierList.sort((supplier1: SupplierDetailsModel, supplier2: SupplierDetailsModel) => {
					const direction = ascending ? 1 : -1;
					return (supplier1.name > supplier2.name ? 1 : -1) * direction;
				});
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	getSupplierListAsSelectItems(): Observable<SelectItem[]> {
		return this.getSupplierList().pipe(
			map((suppliers: SupplierDetailsModel[]) => {
				return this.mapSuppliersToSelectItems(suppliers);
			}),
			shareReplay()
		);
	}

	private mapSuppliersToSelectItems(suppliers: SupplierDetailsModel[]): { id: string; value: string }[] {
		return suppliers.map((supplier: SupplierDetailsModel) => {
			return {id: supplier.uuid, value: supplier.name};
		});
	}

	getSupplierById(supplierUuid: string): Observable<SupplierDetailsModel> {
		const url = this.generateUrl(this.services.stock, '%s/%s', 'supplier', supplierUuid);
		return this.http.get<ObjectResponse<SupplierDetailsModel>>(url, this.options).pipe(
			map(
				(res: ObjectResponse<SupplierDetailsModel>) => {
					return res.data;
				}
			)
		);
	}

	createSupplier(supplierCreate: SupplierDetailsModel): Observable<SupplierDetailsModel> {
		const url = this.generateUrl(this.services.stock, 'supplier');

		return this.http.post<ObjectResponse<SupplierDetailsModel>>(url, supplierCreate, this.options).pipe(
			map((response: ObjectResponse<SupplierDetailsModel>) => {
				return response.data;
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	updateSupplier(supplier: SupplierDetailsModel): Observable<SupplierDetailsModel> {
		const url = this.generateUrl(this.services.stock, 'supplier');

		return this.http.put<ObjectResponse<SupplierDetailsModel>>(url, supplier, this.options).pipe(
			map((response: ObjectResponse<SupplierDetailsModel>) => {
				return response.data;
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	deleteSupplier(uuid: string): Observable<void> {
		const url = this.generateUrl(this.services.stock, '%s/%s', 'supplier', uuid);

		return this.http.delete(url, this.options).pipe(
			map(() => {
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	createListViewModels(suppliers: SupplierDetailsModel[], purchaseCosts: PurchaseCost[]): Observable<SupplierListViewModel[]> {
		const listViewModels = suppliers.map((supplier: SupplierDetailsModel) => {
			const purchaseCost = purchaseCosts.find((cost: PurchaseCost) => {
				return cost.supplierUuid === supplier.uuid;
			});

			return {
				uuid: supplier.uuid,
				name: supplier.name,
				purchaseCosts: purchaseCost ? purchaseCost.costs : 0,
				deliveryTime: supplier.deliveryTime,
				baselineCustomerId: supplier.baselineCustomerId,
				stockCategory: supplier.stockCategory,
			};
		});
		return of(listViewModels);
	}

	getStockCategorySelectItems(): Observable<SelectItem[]> {
		if (!this.stockCategoriesSelectItems) {
			this.initStockCategories();
		}

		return of(this.stockCategoriesSelectItems).pipe(
			map((categories: SelectItem[]) => {
				return categories.filter((category: SelectItem) => {
					return category.id !== StockCategories.VIRTUAL; // VirtualStock is never selectable - its a technical type of stock
				});
			})
		);
	}

	getStockCategoryDisplayName(stockCategory: StockCategories): string {
		return this.stockCategoryNames.get(stockCategory) || '';
	}

	private initStockCategories(): void {
		this.stockCategoriesSelectItems = Array.from(this.stockCategoryNames.entries())
			.map((entry: [number, string]) => {
				return {id: entry[0], value: entry[1]};
			});
	}

	getNonDistributorSuppliersAsSelectItem(): Observable<SelectItem[]> {
		return this.getSupplierListSortedByName().pipe(
			map((suppliers: SupplierDetailsModel[]) => {
				const filtered = suppliers.filter((s: SupplierDetailsModel) => {
					return s.stockCategory !== StockCategories.DISTRIBUTOR;
				});

				return this.mapSuppliersToSelectItems(filtered);
			})
		);
	}
}
